import type { Issue } from '../../api/DigiobcanApi';
import { ReportStatusChip } from '../../components/AdminReportStatusItem';
import { ExplicitIcon } from '../../components/ExplicitIcon';
import { meGetReports } from '../../features/queries/meGetReports';
import { shortId } from '../../lib/ids';
import { useInfiniteQ, useLoadPagesThrough } from '../../lib/query';
import { useResponsive } from '../../providers/ResponsiveProvider';
import { __ } from '../../utils';
import ReportDetailSheet from '../podnety/[id]/ReportDetailSheet';
import ReportDetailDrawer from './ReportDetailDrawer';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  Box,
  DataTable,
  EmptyState,
  Pagination,
  Stack,
  Tooltip,
  Typography,
  useUrlFilters,
  type Column,
} from '@presov.sk/presov-ui-react';
import { formatDate } from 'date-fns';
import React from 'react';
import { useSearchParams } from 'react-router';

const LIMIT = 15;

type MyReport = {
  id: string;
  issue_id?: string;
  title: string;
  inserted_at: string;
  accepted?: boolean;
  /**
   * Stav podnetu odvodený zo stavov jeho nálezov - vyhráva najmenej pokročilý.
   *
   * **Je nepovinný zámerne:** `MyReportListItem` ho v `digiobcan-api` dostal až
   * teraz, takže staršie API ho neposiela a appka musí zniesť aj odpoveď bez
   * neho. Vtedy sa spadne späť na `accepted` - to bolo dovtedy jediné, čo
   * o stave podnetu vedela ([[reportStatus]]).
   */
  status?: Issue['status'];
  explicit?: boolean;
  votes_count?: number;
};

const EXPLICIT_TOOLTIP = __(
  'Tento podnet vidí iba administrátor. Nevidia ho ostatní používatelia, pretože obsahuje nevhodný obsah, ako napríklad nevhodné fotografie, vulgarizmy, urážky alebo iné nevhodné výrazy. Ak sa vám zdá, že je označenie nesprávne, kontaktujte nás.',
);

/**
 * Stav podnetu z pohľadu občana. Nie je to stav nálezu - občan nálezy nevidí,
 * vidí podnet ako celok, takže stav je z nich odvodený na API (vyhráva
 * najmenej pokročilý; podnet je vyriešený až vtedy, keď je vyriešený každý
 * jeho nález).
 *
 * Občianske stavy sú tri: **Podané**, **V riešení**, **Vyriešené**. Interné
 * rozhodnutia úradu sa do nich vlievajú - `duplicate` je „v riešení" (niekto
 * to má v ruke, len pod iným podnetom) a `moved` je „podané" (nález odišiel
 * inam a jeho nový domov ešte nezačal). Štítok je ten istý `ReportStatusChip`
 * ako v administrácii: je to ten istý stav a nemá mať dve podoby.
 *
 * Štvrtý stav („Zaznamenáva sa", teda podnet pred dobehnutím spracovania
 * v `reports-processor`) tu bol a je preč zámerne. Bol to interný stav
 * mašinérie: občan z neho nič nevyčítal - podnet podal a mesto ho ešte
 * nepridelilo, presne ako pri „Podané" - a menil sa mu sám od seba pod rukami.
 */
const reportStatus = (
  report: MyReport,
): { label: string; status: Issue['status']; tooltip: string } => {
  // Bez `status` z API vieme len to, či podnet niekto vzal do ruky.
  const status = report.status ?? (report.accepted ? 'accepted' : 'open');

  if (status === 'resolved') {
    return {
      label: __('Vyriešené'),
      status: 'resolved',
      tooltip: __('Mesto podnet uzavrelo. Vyjadrenie nájdete v detaile.'),
    };
  }

  if (status === 'accepted' || status === 'duplicate') {
    return {
      label: __('V riešení'),
      status: 'accepted',
      tooltip: __('Podnet bol priradený pracovníkovi a rieši sa.'),
    };
  }

  return {
    label: __('Podané'),
    status: 'open',
    tooltip: __('Podnet čaká na pridelenie zodpovednému útvaru.'),
  };
};

/**
 * Sekcia „Moje podnety" - predvolená a prvá sekcia profilu.
 *
 * Bola to samostatná stránka `/moje-podnety` s vlastnou položkou v hlavnom
 * menu; z menu zmizla a zoznam sa presťahoval sem. Adresa zostáva a
 * presmeruje ([[App]]), lebo na ňu ukazujú rozposlané odkazy.
 *
 * **Zoznam nemá okolo seba kartu.** `DataTable` si biely podklad, rám aj
 * zaoblenie kreslí sám a stránkovanie berie do vlastnej pätičky (`footer`),
 * takže karta okolo neho bola druhý rám okolo prvého - tabuľka v tabuľke.
 * Ostatné sekcie profilu kartu majú, ich obsah si rám nenesie.
 *
 * **Filtre stavov z dizajnového balíka (Všetky / V riešení / Vyriešené /
 * Zamietnuté) tu nie sú a nedopisujte ich na klientovi.** `/me/reports` pozná
 * len `limit` a `offset` a počty na chipy nevracia; filter nad načítanou
 * stranou by prehľadal pätnásť riadkov a tváril sa, že prehľadal zoznam
 * ([zoznamy.md](../../../.docs/vyvoj/zoznamy.md)). „Zamietnuté" navyše nie je
 * stav, ktorý by DigiObčan poznal.
 */
const MyReports: React.FC = () => {
  const { isMd } = useResponsive();
  const { authState } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const { page: pageIndex, setPage: setPageIndex } = useUrlFilters({
    searchParams,
    setSearchParams,
  });

  const {
    data = { pages: [] },
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQ(
    meGetReports,
    { limit: LIMIT },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) =>
        lastPage.hasNextOffset ? lastPage.offset + lastPage.limit : undefined,
      enabled: authState === 'authorized',
    },
  );

  // F5 na `?page=2`: nekonečný dotaz začína od prvej strany, takže sa musí
  // dočítať až po tú z adresy (`useLoadPagesThrough`).
  useLoadPagesThrough({
    pageIndex,
    loadedPages: data.pages.length,
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || isFetchingNextPage,
    fetchNextPage,
    setPageIndex,
  });

  const reports = (data.pages[pageIndex]?.reports ?? []) as MyReport[];

  const columns: Column<MyReport>[] = React.useMemo(
    () => [
      {
        key: 'id',
        label: __('ID'),
        // Identifikátor má **vždy** 7 znakov (`platform.identifier_length`
        // v `digiobcan-api`) a predpona sa v bunke nekreslí, takže stĺpec je
        // pevný, nie pružný - `fr` mu bralo šírku podľa okna a zvyšok riadku
        // o ňu prichádzal.
        width: '92px',
        minWidth: '92px',
        mono: true,
        // Len koncový segment - predpona `pdnt:` je v celom stĺpci rovnaká
        // a rovnaká je aj v adrese, kam riadok vedie.
        render: (report) => shortId(report.id),
      },
      {
        key: 'title',
        label: __('Názov'),
        width: '2.6fr',
        minWidth: '220px',
        strong: true,
        render: (report) => (
          <Stack direction='row' gap={0.5} alignItems='center'>
            {report.explicit ? (
              <ExplicitIcon tooltip={EXPLICIT_TOOLTIP} />
            ) : null}
            <Typography variant='body2'>{report.title}</Typography>
          </Stack>
        ),
      },
      {
        key: 'inserted_at',
        label: __('Dátum vytvorenia'),
        width: '1.2fr',
        minWidth: '160px',
        render: (report) =>
          formatDate(new Date(report.inserted_at), 'dd.MM.yyyy HH:mm'),
      },
      {
        key: 'status',
        label: __('Stav'),
        width: '1.1fr',
        minWidth: '150px',
        render: (report) => {
          const { label, status, tooltip } = reportStatus(report);

          return (
            <Tooltip title={tooltip}>
              <Box component='span' sx={{ display: 'inline-flex' }}>
                <ReportStatusChip status={status} label={label} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        // Hlasy sú aj tu jedno číslo bez skloňovania - stĺpec je numerický
        // a čísla sa porovnávajú okom pod sebou, nie čítaním. Rovnako
        // v administračnom zozname podnetov.
        key: 'votes_count',
        label: __('Hlasy'),
        width: '0.6fr',
        minWidth: '80px',
        numeric: true,
        // Nula je platný počet, nie chýbajúca hodnota - viď rovnaký stĺpec
        // v administračnom zozname podnetov.
        render: (report) => {
          const votes = report.votes_count ?? 0;
          return (
            <Typography
              variant='body2'
              color={votes > 0 ? 'text.primary' : 'text.disabled'}
            >
              {votes}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  /**
   * Otvorený detail žije v adrese zoznamu (`?detail=MQRJ73F`), rovnako ako nad
   * mapou na hlavnej stránke - takže ho zavrie tlačidlo Späť a odkaz sa dá
   * poslať. Na desktope sa vysunie sprava ([[ReportDetailDrawer]]), na mobile
   * zdola ako sheet ([[ReportDetailSheet]]).
   */
  const detailShortId = searchParams.get('detail') ?? undefined;

  const closeDetail = React.useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete('detail');
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const openDetail = React.useCallback(
    (report: MyReport) => {
      // Otvorenie je krok navigácie (bez `replace`), aby ho tlačidlo Späť
      // v prehliadači zavrelo; zatvorenie je `replace`, inak by sa história
      // zaplnila preklikanými podnetmi.
      setSearchParams((params) => {
        params.set('detail', shortId(report.id));
        return params;
      });
    },
    [setSearchParams],
  );

  const hasNext =
    (pageIndex < data.pages.length - 1 || hasNextPage) && !isFetchingNextPage;

  return (
    <>
      {/* Bez vlastného obalu: rám aj odsadenie okolo tabuľky dáva sekcia
          profilu, skrolovanie jej stĺpec ([[ProfilePage]]). */}
      <DataTable
        variant={isMd ? 'table' : 'cards'}
        columns={columns}
        rows={reports}
        rowKey='id'
        // Oboje: `rowHref` je verejná adresa podnetu (cmd klik = nová karta),
        // `onRowClick` obyčajným klikom otvorí detail pri zozname - odchod na
        // mapu bol zo zoznamu vlastných podnetov cesta preč od toho, kvôli
        // čomu doň občan prišiel.
        rowHref={(report) => `/podnety/${shortId(report.id)}`}
        onRowClick={openDetail}
        loading={isLoading || isFetchingNextPage}
        empty={
          <EmptyState
            compact
            icon='list'
            title={__('Žiadne podnety')}
            description={__(
              'Zatiaľ ste nepodali žiadny podnet. Nový podnet vytvoríte tlačidlom v hlavičke.',
            )}
          />
        }
        footer={
          // V `cards` režime tabuľka pätičku len položí pod karty bez pásu,
          // takže si odsadenie od nich nesie stránkovanie samo.
          <Box sx={{ width: '100%', pt: isMd ? 0 : 1.5 }}>
            <Pagination
              page={pageIndex}
              hasNext={hasNext}
              onPageChange={(next) => {
                if (next >= data.pages.length && hasNextPage) {
                  fetchNextPage();
                }
                setPageIndex(next);
              }}
            />
          </Box>
        }
      />

      {detailShortId ? (
        isMd ? (
          <ReportDetailDrawer
            shortReportId={detailShortId}
            onClose={closeDetail}
          />
        ) : (
          <ReportDetailSheet
            shortReportId={detailShortId}
            onClose={closeDetail}
          />
        )
      ) : null}
    </>
  );
};

export default MyReports;
